import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Importación de Páginas
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import SharePage from './pages/SharePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LegalPage from './pages/LegalPage';
import AdminPanel from './pages/AdminPanel';
import SchoolAdminPanel from './pages/SchoolAdminPanel'; 
import InvitePage from './pages/InvitePage';

// Iconos y Utilidades
import { Mail, Loader2, LogOut } from 'lucide-react'; 
import { toast } from 'sonner'; 
import './App.css';

// PANTALLA DE BLOQUEO ANTI-ESTAFADORES
function VerifyEmailScreen() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { verifyEmail, logout, user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error('El código debe tener 6 números');
    
    setLoading(true);
    try {
      await verifyEmail(code);
      toast.success('¡Cuenta verificada! Ya tenés tus 10 créditos.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código incorrecto. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl w-full max-w-md shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-violet-600" />
        
        <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 border border-blue-500/20">
          <Mail size={28} className="text-blue-400" />
        </div>
        
        <h2 className="text-2xl font-bold text-white mb-2">Revisá tu correo</h2>
        <p className="text-slate-400 text-sm mb-6">
          Te enviamos un código de 6 dígitos a <strong className="text-white">{user?.email}</strong>. 
          Ingresalo abajo para activar tus 10 créditos gratis.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} // Solo permite números
              placeholder="123456"
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-4 text-center text-3xl tracking-[0.5em] text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-mono"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Verificando...</> : 'Verificar cuenta'}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-slate-800 text-center">
          <button 
            onClick={logout}
            className="text-slate-500 hover:text-slate-300 text-sm flex items-center justify-center gap-2 w-full transition-colors"
          >
            <LogOut size={14} /> ¿Te equivocaste de correo? Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}

// EL PATOVICA DEL SISTEMA
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-slate-700 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Cargando DinamiClass...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  
  if (!user.is_verified && user.role !== 'admin' && user.role !== 'school_admin') {
    return <VerifyEmailScreen />;
  }
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/share/:shareId" element={<SharePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          
          {/* NUEVA RUTA B2B CORREGIDA (Usando ProtectedRoute) */}
          <Route path="/school-admin" element={<ProtectedRoute><SchoolAdminPanel /></ProtectedRoute>} />
          <Route path="/invite/:slug" element={<InvitePage />} />
          
          {/* El comodín (*) SIEMPRE al final */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
